<script setup>
import { ref, computed, watch, nextTick } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  assetId: {
    type: String,
    default: '',
  },
  assetUrl: {
    type: String,
    default: '',
  },
  currentTag: {
    type: String,
    default: '',
  },
  isMaterial: {
    type: Boolean,
    default: true,
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
});

const emit = defineEmits(['update', 'close']);

// State
const activeTab = ref(props.isMaterial ? 'material' : 'reference');
const selectedUsage = ref('');
const customUsage = ref('');
const inputRef = ref(null);

// Reference points (same as MaterialMark)
const referencePoints = [
  { id: 'all', label: 'All' },
  { id: 'layout', label: 'Layout' },
  { id: 'style', label: 'Style' },
  { id: 'color', label: 'Color' },
  { id: 'texture', label: 'Texture' },
  { id: 'copy', label: 'Copy' },
  { id: 'font', label: 'Font' },
];

const normalizeString = (value) => (typeof value === 'string' ? value.trim() : '');

const parseReferenceTagList = (tag) => {
  const t = normalizeString(tag);
  if (!t) return [];
  return t
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const selectedPoints = ref(
  !props.isMaterial && props.currentTag
    ? parseReferenceTagList(props.currentTag)
    : !props.isMaterial
      ? referencePoints.map((item) => item.id)
      : []
);

// Material usages (same as MaterialMark)
const materialUsages = [
  { id: 'background', label: 'Background' },
  { id: 'product', label: 'Product' },
  { id: 'model', label: 'Model' },
  { id: 'logo', label: 'Logo' },
  { id: 'qrcode', label: 'QR Code' },
  { id: 'icon', label: 'Icon' },
  { id: 'decoration', label: 'Decoration' },
  { id: 'texture', label: 'Texture' },
  { id: 'chart', label: 'Chart' },
];

const normalizeMaterialTag = (tag) => {
  const t = normalizeString(tag);
  if (!t) return '';

  const lower = t.toLowerCase();
  const byId = materialUsages.find((u) => u.id.toLowerCase() === lower);
  if (byId) return byId.label;

  const byLabel = materialUsages.find((u) => u.label.toLowerCase() === lower);
  if (byLabel) return byLabel.label;

  return t;
};

// Constants
const IMAGE_WIDTH = 160;
const CONTENT_WIDTH = 344; // 280 + 64 (4rem)
const POPOVER_WIDTH = IMAGE_WIDTH + CONTENT_WIDTH;
const POPOVER_HEIGHT = 360;
const VIEWPORT_PADDING = 16;

selectedUsage.value = props.isMaterial ? normalizeMaterialTag(props.currentTag) : '';

// Watchers
watch(
  () => props.show,
  async (show) => {
    if (show) {
      activeTab.value = props.isMaterial ? 'material' : 'reference';
      selectedUsage.value = props.isMaterial ? normalizeMaterialTag(props.currentTag) : '';
      // Reference mode: 如果有 currentTag 则解析，否则默认全选
      if (!props.isMaterial) {
        selectedPoints.value = props.currentTag
          ? parseReferenceTagList(props.currentTag)
          : referencePoints.map((item) => item.id);
      } else {
        selectedPoints.value = [];
      }
      customUsage.value = '';
      await nextTick();
      inputRef.value?.focus?.();
    }
  }
);

// --- Tab Logic ---
function switchToReference() {
  if (activeTab.value === 'reference') return;
  activeTab.value = 'reference';
  // Reset to all points when switching to reference mode
  selectedPoints.value = referencePoints.map((item) => item.id);
  customUsage.value = '';
  updateAssetAsReference();
}

function switchToMaterial() {
  if (activeTab.value === 'material') return;
  activeTab.value = 'material';
  // Reset to default usage when switching to material mode
  const tag = 'Background';
  selectedUsage.value = tag;
  customUsage.value = '';
  emit('update', { id: props.assetId, tag, isMaterial: true });
}

// --- Material Logic ---
function selectUsage(usage) {
  selectedUsage.value = usage;
  emit('update', { id: props.assetId, tag: usage, isMaterial: true });
  emit('close');
}

function addCustomUsage() {
  if (customUsage.value.trim()) {
    selectedUsage.value = customUsage.value.trim();
    emit('update', { id: props.assetId, tag: customUsage.value.trim(), isMaterial: true });
    customUsage.value = '';
    emit('close');
  }
}

function isUsageSelected(usage) {
  return activeTab.value === 'material' && selectedUsage.value === usage;
}

// --- Reference Logic ---
const allReferencePointsExceptAll = referencePoints
  .filter((item) => item.id !== 'all')
  .map((item) => item.id);

function toggleReferencePoint(point) {
  if (point === 'all') {
    if (selectedPoints.value.includes('all')) {
      selectedPoints.value = [];
    } else {
      selectedPoints.value = referencePoints.map((item) => item.id);
    }
  } else {
    const index = selectedPoints.value.indexOf(point);

    if (index === -1) {
      selectedPoints.value.push(point);
      if (allReferencePointsExceptAll.every((id) => selectedPoints.value.includes(id))) {
        if (!selectedPoints.value.includes('all')) {
          selectedPoints.value.push('all');
        }
      }
    } else {
      selectedPoints.value.splice(index, 1);
      const allIndex = selectedPoints.value.indexOf('all');
      if (allIndex !== -1) {
        selectedPoints.value.splice(allIndex, 1);
      }
    }
  }

  updateAssetAsReference();
}

function updateAssetAsReference() {
  const tag = selectedPoints.value
    .map((id) => referencePoints.find((item) => id === item.id))
    .filter((item) => item)
    .map((item) => item.id)
    .join(',');
  emit('update', { id: props.assetId, tag, isMaterial: false });
}

function isPointSelected(point) {
  return activeTab.value === 'reference' && selectedPoints.value.includes(point);
}

// Safe position calculation
const safePosition = computed(() => {
  const { x, y } = props.position;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const halfWidth = POPOVER_WIDTH / 2;
  let safeX = x;

  if (x - halfWidth < VIEWPORT_PADDING) {
    safeX = halfWidth + VIEWPORT_PADDING;
  }
  if (x + halfWidth > viewportWidth - VIEWPORT_PADDING) {
    safeX = viewportWidth - halfWidth - VIEWPORT_PADDING;
  }

  const spaceBelow = viewportHeight - y - VIEWPORT_PADDING;
  // If space below is less than POPOVER_HEIGHT and space above is more than space below, show above
  const showAbove = spaceBelow < POPOVER_HEIGHT && y > spaceBelow;

  return { x: safeX, y, showAbove };
});

const popoverStyle = computed(() => {
  const { x, y, showAbove } = safePosition.value;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const style = {
    left: `${x - POPOVER_WIDTH / 2}px`,
    width: `${POPOVER_WIDTH}px`,
  };

  if (showAbove) {
    style.bottom = `${viewportHeight - y + 8}px`;
  } else {
    style.top = `${y + 8}px`;
  }

  return style;
});

const handleKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    addCustomUsage();
  } else if (e.key === 'Escape') {
    e.preventDefault();
    emit('close');
  }
};
</script>

<template>
  <Teleport to="body">
    <!-- Transparent Backdrop for click-outside -->
    <div v-if="show" class="fixed inset-0 z-[99]" @click="emit('close')" />

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="show"
        class="material-popover fixed z-[100] bg-white border-[0.5px] border-black/15 rounded-2xl overflow-hidden flex flex-row"
        :style="[popoverStyle, { width: `${POPOVER_WIDTH}px`, maxHeight: '400px' }]"
        @click.stop
        @mousedown.stop
      >
        <!-- Left: Image Preview -->
        <div
          class="flex-none bg-black/[0.02] flex items-center justify-center p-3"
          :style="{ width: `${IMAGE_WIDTH}px`, maxHeight: '400px' }"
        >
          <div class="w-full rounded-xl overflow-hidden" style="max-height: calc(400px - 24px)">
            <img
              :src="assetUrl"
              alt="Preview"
              class="w-full h-full object-contain"
              style="max-height: calc(400px - 24px)"
            />
          </div>
        </div>

        <!-- Right: Cards and Options -->
        <div
          class="flex-1 p-3 flex flex-col overflow-y-auto minimal-scrollbar"
          :style="{ width: `${CONTENT_WIDTH}px` }"
        >
          <!-- Title -->
          <h3 class="text-black/40 font-normal text-sm mb-2 px-2">Use as</h3>

          <!-- Card Selection -->
          <div class="flex gap-2 mb-4">
            <!-- Design Reference Card -->
            <button
              type="button"
              @click="switchToReference"
              :class="[
                'flex-1 p-3 rounded-lg border transition-all duration-200 text-left flex flex-col items-start gap-2',
                activeTab === 'reference'
                  ? 'bg-white border-transparent shadow-[0_4px_12px_rgba(254,113,59,0.08)]'
                  : 'bg-transparent border-black/5 hover:border-black/10',
              ]"
              :style="activeTab === 'reference' ? 'box-shadow: 0 0 0 1px #FE713B' : ''"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
                  activeTab === 'reference'
                    ? 'bg-[#FE713B]/10 text-[#FE713B]'
                    : 'bg-black/5 text-black/40',
                ]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <span
                :class="[
                  'text-sm font-medium transition-colors duration-200',
                  activeTab === 'reference' ? 'text-black/90' : 'text-black/60',
                ]"
                style="line-height: 1.2"
              >
                Design reference
              </span>
            </button>

            <!-- My Assets Card -->
            <button
              type="button"
              @click="switchToMaterial"
              :class="[
                'flex-1 p-3 rounded-lg border transition-all duration-200 text-left flex flex-col items-start gap-2',
                activeTab === 'material'
                  ? 'bg-white border-transparent shadow-[0_4px_12px_rgba(254,113,59,0.08)]'
                  : 'bg-transparent border-black/5 hover:border-black/10',
              ]"
              :style="activeTab === 'material' ? 'box-shadow: 0 0 0 1px #FE713B' : ''"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
                  activeTab === 'material'
                    ? 'bg-[#FE713B]/10 text-[#FE713B]'
                    : 'bg-black/5 text-black/40',
                ]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
              <span
                :class="[
                  'text-sm font-medium transition-colors duration-200',
                  activeTab === 'material' ? 'text-black/90' : 'text-black/60',
                ]"
                style="line-height: 1.2"
              >
                My assets in design
              </span>
            </button>
          </div>

          <!-- Reference Options -->
          <div v-if="activeTab === 'reference'" class="flex-1">
            <div class="text-black/40 text-sm mb-2 px-2">Reference points</div>
            <div class="grid grid-cols-2 gap-1.5">
              <button
                v-for="point in referencePoints"
                :key="point.id"
                :class="[
                  'group/tag flex items-center gap-2 p-2 rounded-lg border border-black/5 hover:border-black/10 bg-transparent transition-all duration-200 text-left focus-visible:outline-none active:scale-[0.98]',
                  point.id === 'all' ? 'col-span-2' : '',
                ]"
                @click="toggleReferencePoint(point.id)"
                type="button"
              >
                <!-- Checkbox -->
                <div
                  :class="[
                    'w-4 h-4 rounded flex items-center justify-center border-2 transition-all duration-200 flex-shrink-0',
                    isPointSelected(point.id)
                      ? 'bg-[#FE713B] border-[#FE713B]'
                      : 'bg-transparent border-black/20',
                  ]"
                >
                  <svg
                    v-if="isPointSelected(point.id)"
                    class="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span
                  :class="[
                    'text-sm font-medium transition-colors duration-200',
                    isPointSelected(point.id) ? 'text-black/90' : 'text-black/60',
                  ]"
                  style="line-height: 1.2"
                >
                  {{ point.label }}
                </span>
              </button>
            </div>
          </div>

          <!-- Material Options -->
          <div v-if="activeTab === 'material'" class="flex-1 flex flex-col">
            <div class="text-black/40 text-sm mb-2 px-2">Asset type</div>
            <div class="grid grid-cols-3 gap-1.5 mb-3">
              <button
                v-for="usage in materialUsages"
                :key="usage.id"
                :class="[
                  'group/tag flex items-start p-2 rounded-lg border transition-all duration-200 text-left focus-visible:outline-none active:scale-[0.98]',
                  isUsageSelected(usage.label)
                    ? 'bg-white border-transparent'
                    : 'bg-transparent border-black/5 hover:border-black/10',
                ]"
                :style="isUsageSelected(usage.label) ? 'box-shadow: 0 0 0 1px #FE713B' : ''"
                @click="selectUsage(usage.label)"
                type="button"
              >
                <span
                  :class="[
                    'text-sm font-medium transition-colors duration-200',
                    isUsageSelected(usage.label) ? 'text-black/90' : 'text-black/60',
                  ]"
                  style="line-height: 1.2"
                >
                  {{ usage.label }}
                </span>
              </button>
            </div>

            <!-- Custom usage input -->
            <div class="mt-auto flex items-center gap-1.5">
              <input
                ref="inputRef"
                v-model="customUsage"
                type="text"
                placeholder="Custom usage"
                class="flex-1 h-8 text-sm px-3 border border-solid border-black/[0.02] dark:border-white/[0.02] rounded-lg bg-black/5 dark:bg-white/5 shadow-none focus:outline-none focus:ring-0 transition-colors placeholder:text-black/30 dark:placeholder:text-white/30"
                @keydown="handleKeydown"
                @click.stop
              />
              <button
                @click="addCustomUsage"
                :disabled="!customUsage.trim()"
                type="button"
                class="h-8 px-3 bg-transparent border border-black/5 text-black/70 text-sm font-medium rounded-xl hover:border-black/10 hover:text-black/90 active:scale-95 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.minimal-scrollbar::-webkit-scrollbar {
  width: 3px;
}

.minimal-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.minimal-scrollbar::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 2px;
}

.minimal-scrollbar:hover::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
}
</style>
